import comms from '../../comms';
import { offlineStorage } from '../../../services/offlineStorage';
import { connectionManager } from '../../../services/connectionManager';

let isInitialized = false;

// Helper function to format minutes to time string
const formatMinutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  const today = new Date();
  today.setHours(hours, mins, 0, 0);
  
  return today.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
};

// Helper function to check if a stop has been visited today for fixed routes
const isStopVisitedToday = (stop, today) => {
  if (!stop.LastUpdated) return false;
  
  try {
    const lastUpdatedString = stop.LastUpdated.replace('Z', '');
    const lastUpdated = new Date(lastUpdatedString);
    const lastUpdatedDateString = lastUpdated.toDateString();
    
    return lastUpdatedDateString === today;
  } catch (error) {
    console.error('Error parsing LastUpdated date:', error);
    return false;
  }
};

// Build fixed schedule and find current waypoint
const buildFixedScheduleAndFindCurrent = (fixedRoute, vehicleId) => {
  if (!fixedRoute || !fixedRoute.Stops || !fixedRoute.Runs || !fixedRoute.Schedules) {
    return { daySchedule: [], currentWaypoint: null };
  }

  const now = new Date();
  const todayMinutes = now.getHours() * 60 + now.getMinutes();
  const today = new Date().toDateString();

  console.log('Current time in minutes:', todayMinutes, `(${Math.floor(todayMinutes/60)}:${(todayMinutes%60).toString().padStart(2,'0')})`);

  // Find vehicle's schedules for today
  const dayOfWeek = now.getDay();
  const dayNames = ['ActiveSunday', 'ActiveMonday', 'ActiveTuesday', 'ActiveWednesday', 'ActiveThursday', 'ActiveFriday', 'ActiveSaturday'];
  const todayProperty = dayNames[dayOfWeek];

  // Combine all run schedules for this vehicle from all schedules
  let allRunSchedules = [];
  
  if (fixedRoute.Schedules && Array.isArray(fixedRoute.Schedules)) {
    fixedRoute.Schedules.forEach(schedule => {
      const isForThisVehicle = !schedule.VehicleId || schedule.VehicleId === vehicleId;
      const isActiveToday = schedule[todayProperty];
      
      console.log(`Schedule: ${schedule.Name}, VehicleId: ${schedule.VehicleId}, ForThisVehicle: ${isForThisVehicle}, ActiveToday: ${isActiveToday}`);
      
      if (isForThisVehicle && isActiveToday && schedule.Runschedule && Array.isArray(schedule.Runschedule)) {
        // Only process fixed schedules (not looped)
        schedule.Runschedule.forEach(runSchedule => {
          const run = fixedRoute.Runs.find(r => r.Id === runSchedule.RunId);
          
          if (run && run.Itinerary) {
            const scheduleDuration = runSchedule.EndTime - runSchedule.StartTime;
            const finalStopETA = Math.max(...run.Itinerary.map(stop => stop.ScheduledETA));
            
            // Skip looped schedules
            const isLoopingSchedule = schedule.Runschedule.length === 1 && scheduleDuration > finalStopETA;
            
            if (!isLoopingSchedule) {
              allRunSchedules.push({
                ...runSchedule,
                scheduleName: schedule.Name,
                scheduleId: schedule.Id,
                schedule: schedule
              });
            }
          }
        });
      }
    });
  }

  if (allRunSchedules.length === 0) {
    console.log('No active fixed schedules found for vehicle today');
    return { daySchedule: [], currentWaypoint: null };
  }

  console.log(`Found ${allRunSchedules.length} fixed run schedules for vehicle`);

  // Find the earliest start time and latest end time for schedule status checking
  const earliestStartTime = Math.min(...allRunSchedules.map(rs => rs.StartTime));
  const latestEndTime = Math.max(...allRunSchedules.map(rs => rs.EndTime));
  
  // Check if we're before schedule start
  if (todayMinutes < earliestStartTime) {
    console.log('Current time is before schedule start');
    
    // Build a preview schedule showing first stops of each run
    const daySchedule = [];
    
    allRunSchedules.forEach((runSchedule) => {
      const run = fixedRoute.Runs.find(r => r.Id === runSchedule.RunId);
      if (!run || !run.Itinerary || !Array.isArray(run.Itinerary)) return;

      const firstStop = run.Itinerary[0];
      if (firstStop) {
        const stop = fixedRoute.Stops.find(s => s.Id === firstStop.StopId);
        if (stop) {
          daySchedule.push({
            stop: stop,
            itineraryItem: firstStop,
            run: run,
            schedule: runSchedule.schedule,
            runSchedule: runSchedule,
            scheduledTimeMinutes: runSchedule.StartTime + firstStop.ScheduledETA,
            scheduledTime: formatMinutesToTime(runSchedule.StartTime + firstStop.ScheduledETA),
            stopIndex: 0,
            isVisited: false,
            key: `${run.Id}_${firstStop.StopId}_0`
          });
        }
      }
    });

    return {
      daySchedule: daySchedule,
      currentWaypoint: daySchedule[0] || null,
      scheduleStatus: 'pre-schedule',
      scheduleStartTime: formatMinutesToTime(earliestStartTime),
      scheduleEndTime: formatMinutesToTime(latestEndTime)
    };
  }

  // Check if we're after schedule end
  if (todayMinutes > latestEndTime) {
    console.log('Current time is after schedule end');
    return {
      daySchedule: [],
      currentWaypoint: null,
      scheduleStatus: 'post-schedule',
      scheduleStartTime: formatMinutesToTime(earliestStartTime),
      scheduleEndTime: formatMinutesToTime(latestEndTime)
    };
  }

  // Build complete day schedule - includes all stops for all runs
  const daySchedule = [];
  
  // Sort run schedules by start time first
  allRunSchedules.sort((a, b) => a.StartTime - b.StartTime);
  
  allRunSchedules.forEach((runSchedule, runScheduleIndex) => {
    const run = fixedRoute.Runs.find(r => r.Id === runSchedule.RunId);
    if (!run || !run.Itinerary || !Array.isArray(run.Itinerary)) return;

    console.log(`Processing fixed schedule run: ${run.Name || run.Id} (${runSchedule.scheduleName}) from ${formatMinutesToTime(runSchedule.StartTime)} to ${formatMinutesToTime(runSchedule.EndTime)}`);

    run.Itinerary.forEach((itineraryItem, index) => {
      const stop = fixedRoute.Stops.find(s => s.Id === itineraryItem.StopId);
      if (!stop) return;

      const scheduledTimeMinutes = runSchedule.StartTime + itineraryItem.ScheduledETA;
      
      // Check if this stop has been visited by examining logs
      let isVisited = false;
      if (fixedRoute.Logs && Array.isArray(fixedRoute.Logs)) {
        isVisited = fixedRoute.Logs.some(log => {
          // Check if log matches this stop and is from today
          const logMatches = (log.FixedRoute_StopId === stop.Id || log.StopId === stop.Id) &&
                            (log.RunId === run.Id || log.RunName === run.Name);
          
          if (logMatches && log.CreatedOn) {
            try {
              const logDate = new Date(log.CreatedOn);
              const logDateString = logDate.toDateString();
              return logDateString === today;
            } catch (error) {
              console.error('Error parsing log date:', error);
              return false;
            }
          }
          return false;
        });
      }
      
      // For runs with StartTime = EndTime (final runs), check if we should mark them differently
      const isFinalRun = runSchedule.StartTime === runSchedule.EndTime;
      const runType = isFinalRun ? 'Final' : 'Regular';
      
      // Also check LastUpdated on stop itself as fallback
      if (!isVisited) {
        isVisited = isStopVisitedToday(stop, today);
      }

      const scheduleItem = {
        stop: stop,
        itineraryItem: itineraryItem,
        run: run,
        schedule: runSchedule.schedule,
        runSchedule: runSchedule,
        scheduledTimeMinutes: scheduledTimeMinutes,
        scheduledTime: formatMinutesToTime(scheduledTimeMinutes),
        stopIndex: index,
        totalStopsInRun: run.Itinerary.length,
        isVisited: isVisited,
        isPastTime: todayMinutes > scheduledTimeMinutes,
        runScheduleIndex: runScheduleIndex,
        isFinalRun: isFinalRun,
        runType: runType,
        key: `${runSchedule.scheduleId}_${run.Id}_${runSchedule.StartTime}_${stop.Id}_${index}_${runScheduleIndex}`
      };

      daySchedule.push(scheduleItem);
    });
  });

  // Sort by scheduled time to get chronological order
  daySchedule.sort((a, b) => a.scheduledTimeMinutes - b.scheduledTimeMinutes);

  console.log(`Built complete fixed day schedule with ${daySchedule.length} total stops`);
  
  // Log the schedule for debugging
  daySchedule.forEach((item, index) => {
    console.log(`${index + 1}. ${item.scheduledTime}: ${item.stop.Name} (${item.run.Name} - ${item.runSchedule.scheduleName}) ${item.isVisited ? '[VISITED]' : ''} ${item.isPastTime ? '[PAST]' : ''}`);
  });

  // Find current waypoint - the next unvisited stop that hasn't passed its scheduled time
  let currentWaypoint = null;
  
  // First, try to find the next unvisited stop
  for (const item of daySchedule) {
    if (!item.isVisited) {
      currentWaypoint = item;
      break;
    }
  }

  // If no unvisited stops found, find the next stop that hasn't passed its time
  if (!currentWaypoint) {
    for (const item of daySchedule) {
      if (!item.isPastTime) {
        currentWaypoint = item;
        break;
      }
    }
  }

  // If still no current waypoint found, use the last item in the schedule
  if (!currentWaypoint && daySchedule.length > 0) {
    currentWaypoint = daySchedule[daySchedule.length - 1];
  }

  console.log('Current waypoint:', currentWaypoint ? `${currentWaypoint.scheduledTime} - ${currentWaypoint.stop.Name} (${currentWaypoint.run.Name})` : 'None');

  return {
    daySchedule: daySchedule,
    currentWaypoint: currentWaypoint,
    scheduleStatus: 'active',
    scheduleStartTime: formatMinutesToTime(earliestStartTime),
    scheduleEndTime: formatMinutesToTime(latestEndTime)
  };
};

// Process job data for fixed schedules
const processFixedJobData = (daySchedule, currentWaypoint) => {
  if (!daySchedule || daySchedule.length === 0) {
    return [];
  }

  const jobs = [];
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  // For fixed schedules, we want to show all upcoming stops (not just current waypoint + remaining)
  // This gives the driver a complete view of their entire day's schedule
  
  const upcomingStops = daySchedule.filter(item => {
    // Show stops that haven't been visited and haven't passed their scheduled time
    return !item.isVisited && !item.isPastTime;
  });

  console.log(`Processing ${upcomingStops.length} upcoming stops for job queue`);

  upcomingStops.forEach((item, index) => {
    // Determine if this is the current/next stop
    const isCurrent = currentWaypoint && item.key === currentWaypoint.key;
    
    // Calculate time until scheduled departure
    const minutesUntilScheduled = item.scheduledTimeMinutes - currentMinutes;
    let timeStatus = '';
    if (minutesUntilScheduled > 0) {
      timeStatus = `in ${minutesUntilScheduled} min`;
    } else if (minutesUntilScheduled < 0) {
      timeStatus = `${Math.abs(minutesUntilScheduled)} min ago`;
    } else {
      timeStatus = 'now';
    }

    const job = {
      ...item,
      location: [item.stop.Lat, item.stop.Lon],
      address: item.stop.Name,
      arrival: item.scheduledTime,
      instructions: `${item.run.Name || 'Run'} - Stop ${item.stopIndex + 1}/${item.totalStopsInRun}${item.isFinalRun ? ' (Final)' : ''}`,
      phone: item.stop.Phone || '',
      completed: false,
      isCurrent: isCurrent,
      timeStatus: timeStatus,
      minutesUntilScheduled: minutesUntilScheduled,
      runInfo: `${item.runSchedule.scheduleName}`,
      
      // Additional display properties
      displayTime: item.scheduledTime,
      displayStatus: isCurrent ? 'Next Stop' : minutesUntilScheduled <= 5 ? 'Upcoming' : 'Scheduled',
      isLate: minutesUntilScheduled < 0,
      isImmediate: Math.abs(minutesUntilScheduled) <= 5,
      isFinalRun: item.isFinalRun,
      runType: item.runType
    };

    jobs.push(job);
  });

  // If no upcoming stops, show the last few completed stops for context
  if (jobs.length === 0) {
    const completedStops = daySchedule.filter(item => item.isVisited || item.isPastTime)
                                    .slice(-3); // Last 3 stops

    completedStops.forEach(item => {
      const job = {
        ...item,
        location: [item.stop.Lat, item.stop.Lon],
        address: item.stop.Name,
        arrival: item.scheduledTime,
        instructions: `${item.run.Name || 'Run'} - Stop ${item.stopIndex + 1}/${item.totalStopsInRun}${item.isFinalRun ? ' (Final)' : ''}`,
        phone: item.stop.Phone || '',
        completed: true,
        displayTime: item.scheduledTime,
        displayStatus: item.isVisited ? 'Completed' : 'Missed',
        runInfo: `${item.runSchedule.scheduleName}`,
        isFinalRun: item.isFinalRun,
        runType: item.runType
      };

      jobs.push(job);
    });
  }

  console.log(`Created ${jobs.length} jobs for fixed schedule display`);
  return jobs;
};

// Initialize socket for fixed schedules
const initializeFixedSocket = async (socket, setJobDetails, userSettings, fleetSettings, handleFixedRouteData, isDispatchMode = false, onSocketRouteUpdate = null, skipInitialDataLoad = false) => {
  // If no socket was passed, create one
  if (!socket) {
    socket = await comms.connectSocket(fleetSettings.dispatchUri);
  }
  if (isInitialized) return null;
  if (!socket) return null;

  let currentDaySchedule = [];
  let currentWaypointKey = null;

  // Only send initial START command if we're not skipping initial data load
  if (!skipInitialDataLoad) {
    comms.sendSocket('START');
  }

  socket.on('Command', function (msg) {
    console.log('Fixed socket received message:', msg);
    
    try {
      // Handle empty or malformed messages
      if (!msg || typeof msg !== 'string') {
        console.warn('Received invalid message:', msg);
        return;
      }

      const messageParts = msg.split("|");
      
      // Acknowledge message if it has an ID
      if (messageParts[0]) {
        comms.sendSocket("ACK|" + messageParts[0]);
      }

      // Handle PING message - respond with current GPS
      if (messageParts[1] === 'PING') {
        console.log('Received PING, sending GPS response');
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { latitude, longitude } = position.coords;
              comms.sendGPS(latitude, longitude, comms.mpsToMph(position.coords.speed));
              console.log('GPS sent in response to PING:', { latitude, longitude });
            },
            (error) => {
              console.error('Error getting location for PING response:', error);
            },
            { enableHighAccuracy: true, timeout: 5000, maximumAge: 30000 }
          );
        }
        return;
      }

      // Handle START messages or messages without proper JSON
      if (messageParts[1] === 'START' || !messageParts[2]) {
        console.log('START message or message without data');
        return;
      }

      let msgObj;
      try {
        msgObj = JSON.parse(messageParts[2]);
      } catch (parseError) {
        console.warn('Failed to parse message JSON:', messageParts[2], parseError);
        return;
      }

      // Handle special job messages (for dispatch mode)
      if ((messageParts[1] === "JOBM" || messageParts[1] === "JOBOM") && messageParts[3]) {
        try {
          msgObj = JSON.parse(messageParts[3]);
          msgObj.tripNumber = messageParts[2];
        } catch (parseError) {
          console.warn('Failed to parse job message JSON:', messageParts[3], parseError);
          return;
        }
      }

      const result = {
        messageID: messageParts[0],
        command: messageParts[1],
        parameter: msgObj
      };

      console.log('Fixed socket processed command:', result.command);

      // Handle job updates for dispatch mode
      if (isDispatchMode && (result.command === 'STARTM' || result.command === 'JOBM')) {
        // Process regular job data for dispatch mode
        console.log('Processing job data for dispatch mode');
        // TODO: Add job processing logic for dispatch mode if needed
        // const processedData = processJobData(result.command === 'JOBM'?[result.parameter]:result.parameter);
        // setJobDetails(processedData);
      }
      else if (result.command === 'WAYPOINT_UPDATE') {
        console.log('Waypoint update for fixed schedule:', result.parameter);
        
        if (currentDaySchedule.length > 0) {
          // Find and advance to next waypoint in fixed schedule
          const updatedSchedule = currentDaySchedule.map(item => {
            if (item.key === currentWaypointKey) {
              return { ...item, isVisited: true };
            }
            return item;
          });
          
          // Find next unvisited waypoint
          const nextWaypoint = updatedSchedule.find(item => !item.isVisited);
          
          currentDaySchedule = updatedSchedule;
          
          if (nextWaypoint) {
            currentWaypointKey = nextWaypoint.key;
            
            // Update job details
            const jobs = processFixedJobData(updatedSchedule, nextWaypoint);
            setJobDetails(jobs);
          } else {
            // No more waypoints - schedule complete
            console.log('Fixed schedule completed');
            setJobDetails([]);
          }
        }
      }
      else if (result.command === 'FIXEDSTOP_ARRIVE' || result.command === 'FIXEDSTOP_DEPART') {
        // Handle arrivals and departures for fixed routes (server confirmations)
        console.log(`Fixed socket processing ${result.command} confirmation`);
        
        // Parse the arrival/departure data
        let messageData;
        try {
          // For FIXEDSTOP commands, the parameter should already be parsed JSON
          messageData = typeof result.parameter === 'string' ? JSON.parse(result.parameter) : result.parameter;
        } catch (error) {
          console.error('Error parsing FIXEDSTOP message data:', error);
          return;
        }
        
        const action = result.command === 'FIXEDSTOP_ARRIVE' ? 'ARRIVE' : 'DEPART';
        
        // Create socket update data to notify the component
        const updateData = {
          action: action,
          stopId: messageData.stopId,
          itineraryId: messageData.itineraryId,
          passengerCount: messageData.passengerCount || 0,
          runName: messageData.runName,
          runId: messageData.runId,
          scheduleId: messageData.scheduleId,
          driverId: messageData.driverId,
          vehicleId: messageData.vehicleId,
          routeId: messageData.routeId,
          timestamp: new Date().toISOString(),
          command: result.command
        };
        
        console.log(`Fixed route ${action} confirmation for stop ${messageData.stopId}:`, updateData);
        
        // Notify the component via the socket update callback
        if (onSocketRouteUpdate && typeof onSocketRouteUpdate === 'function') {
          console.log('Calling onSocketRouteUpdate for FIXEDSTOP confirmation');
          onSocketRouteUpdate(updateData);
        } else {
          console.log('No onSocketRouteUpdate callback available for FIXEDSTOP confirmation');
        }
      }
      else if (result.command === 'fixedRouteStopUpdate') {
        // Handle socket route updates for break/endpoint management
        console.log('Fixed route stop update received:', result.parameter);
        if (onSocketRouteUpdate && typeof onSocketRouteUpdate === 'function') {
          onSocketRouteUpdate(result.parameter);
        }
      }
      else if (result.command === 'JOB_UPDATE' && isDispatchMode) {
        // Handle regular job updates in dispatch mode
        console.log('Job update in dispatch mode:', result.parameter);
        // Process regular job data here if needed
      }
    } catch (error) {
      console.error('Error processing fixed socket message:', error);
    }
  });

  // Add direct listener for fixedRouteStopUpdate events (like loopedSocketHelper)
  socket.on('fixedRouteStopUpdate', function (updateData) {
    console.log('Received direct fixedRouteStopUpdate for fixed route:', updateData);
    
    try {
      // Call the socket route update callback if provided
      if (onSocketRouteUpdate && typeof onSocketRouteUpdate === 'function') {
        console.log('Calling onSocketRouteUpdate callback with:', updateData);
        onSocketRouteUpdate(updateData);
      } else {
        console.log('No onSocketRouteUpdate callback provided');
      }
    } catch (error) {
      console.error('Error processing direct fixedRouteStopUpdate:', error);
    }
  });

  // Handle socket reconnection - log for debugging
  socket.on('reconnect', function (attemptNumber) {
    console.log('Fixed socket reconnected after', attemptNumber, 'attempts - listeners remain attached');
  });

  isInitialized = true;

  return () => {
    if (socket) {
      socket.off('Command');
      socket.off('fixedRouteStopUpdate');
      socket.off('reconnect');
    }
    isInitialized = false;
  };
};

export { 
  initializeFixedSocket, 
  buildFixedScheduleAndFindCurrent, 
  processFixedJobData,
  formatMinutesToTime 
};
